import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'rms-date',
  standalone: true,
  templateUrl: './date-field.component.html',
  styleUrls: ['../rms-styles.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RmsDateComponent),
      multi: true
    }
  ],
  imports: [CommonModule, FormsModule]
})
export class RmsDateComponent implements ControlValueAccessor {
  @Input() label: string = 'Select Date';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;

  // NEW — capacity-check support (5.2.27's "no override, gray out if closed").
  // blockedDates is a list of 'yyyy-MM-dd' strings the caller has already
  // determined are at/over the bag threshold or past cutoff for the relevant
  // warehouse; minDate optionally prevents picking anything before today.
  @Input() blockedDates: string[] = [];
  @Input() minDate: string | null = null;

  selectedDate: string = ''; // Always store as yyyy-MM-dd string
  errorMessage: string = '';

  onChange = (_: any) => { };
  onTouched = () => { };

  writeValue(value: any): void {
    this.errorMessage = '';

    if (!value) {
      this.selectedDate = '';
      return;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      this.selectedDate = value;
      return;
    }

    if (value instanceof Date) {
      const yyyy = value.getFullYear();
      const mm = String(value.getMonth() + 1).padStart(2, '0');
      const dd = String(value.getDate()).padStart(2, '0');
      this.selectedDate = `${yyyy}-${mm}-${dd}`;
      return;
    }

    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      const yyyy = parsed.getUTCFullYear();
      const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(parsed.getUTCDate()).padStart(2, '0');
      this.selectedDate = `${yyyy}-${mm}-${dd}`;
    } else {
      this.selectedDate = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onDateChange(event: any) {
    const value = event.target.value; // yyyy-MM-dd from <input type="date">

    // NEW — reject blocked dates and anything before minDate. Reverts the
    // input back to its previous value so nothing invalid ever reaches the
    // parent form, and shows an inline warning explaining why.
    if (this.blockedDates.includes(value)) {
      this.errorMessage = 'This date is closed — the warehouse has reached its daily bag limit.';
      event.target.value = this.selectedDate;
      return;
    }

    if (this.minDate && value < this.minDate) {
      this.errorMessage = 'This date has already passed the pickup cutoff.';
      event.target.value = this.selectedDate;
      return;
    }

    this.errorMessage = '';
    this.selectedDate = value;
    this.onChange(value);
    this.onTouched();
  }
}
